import { useLayoutEffect, useRef, type KeyboardEvent } from 'react';

const TOUCH = '(pointer: coarse)';

/**
 * A chat's field to write in (docs/features/mobile.md#chats): it grows with the text up to its max height. With a
 * keyboard Enter sends and Shift+Enter makes a new line; on a touch screen Enter makes a new line and the button sends,
 * as in chat apps, and the placeholder leaves out the keyboard hint.
 */
export function useChatInput(value: string, submit: () => void) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const touch = typeof window !== 'undefined' && window.matchMedia(TOUCH).matches;

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + (el.offsetHeight - el.clientHeight)}px`;
  }, [value]);

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !touch) {
      e.preventDefault();
      submit();
    }
  };

  /** The placeholder without "(Enter to send …)" where Enter does not send. */
  const placeholder = (text: string) => (touch ? text.replace(/\s*\([^)]*\)\s*$/, '') : text);

  return { ref, onKeyDown, placeholder, touch };
}
